/**
 * InsuranceExpirationBannerController — EPIC-034 / 34.5.
 *
 * Bandeau d'alerte d'expiration d'assurance (RC Pro / décennale).
 * Affiche un bandeau rouge (`nm-banner--critical`) pour une attestation
 * expirée, un bandeau orange (`nm-banner--warning`) pour une
 * échéance < 60 jours. Aucun bandeau au-delà.
 *
 * Source : GET /organization/insurance-alerts (livré ce sous-EPIC).
 * Logique de déduplication §2.F ↔ §23.G portée par
 * `InsuranceAlertService` côté backend.
 *
 * Convention CLAUDE.md respectée :
 *   - 0 HTML inline en JS (cloneTemplate + fillSlots + textContent +
 *     classList).
 *   - Aucune classe CSS inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « RC Pro » / « décennale », pas de
 *     « PDP / NotaFacto / Sweego ».
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const CATEGORY_LABELS = {
  rc_pro: "RC Pro",
  decennale: "décennale",
};

const STATUS_BANNER_CLASS = {
  expired: "nm-banner nm-banner--critical",
  expiring_soon: "nm-banner nm-banner--warning",
};

export default class InsuranceExpirationBannerController extends BaseController {
  async connect() {
    try {
      const data = await api.getInsuranceAlerts();
      this.renderAlerts(data.alerts || []);
    } catch (_error) {
      // Silencieux : un bandeau d'alerte qui ne charge pas ne doit pas
      // casser la page hôte. Le calendrier dashboard (§15) reste la
      // source officielle ; ce bandeau est un raccourci de remontée.
    }
  }

  renderAlerts(alerts) {
    for (const alert of alerts) {
      if (alert.status !== "expired" && alert.status !== "expiring_soon") {
        continue;
      }
      const fragment = this.cloneTemplate("tpl-insurance-expiration-banner");
      if (fragment === null) {
        continue;
      }

      const root = fragment.querySelector('[data-slot="banner-root"]');
      if (root !== null) {
        root.className = STATUS_BANNER_CLASS[alert.status];
      }

      const categoryLabel = CATEGORY_LABELS[alert.category] || alert.category;
      const formattedDate = this.formatShortDate(alert.expirationDate);
      const message = alert.status === "expired"
        ? `Renouvelez votre attestation ${categoryLabel} — expirée depuis le ${formattedDate}`
        : `Votre attestation ${categoryLabel} expire le ${formattedDate}`;
      this.fillSlots(fragment, { message });

      const link = fragment.querySelector('[data-slot="update-link"]');
      if (link !== null) {
        link.href = alert.documentId
          ? `documents-liste.html#doc-${encodeURIComponent(alert.documentId)}`
          : "documents-liste.html";
      }

      this.element.appendChild(fragment);
    }
  }
}
